/**
 * sessionBack — how "Back" works inside a session.
 *
 * THE RULE
 * Back goes to the screen the person was on immediately before, with what they
 * wrote there still in place. The order is the order THIS person took, which
 * differs every time: lenses, skips, the insight cards and the Satori moment all
 * change it. So nobody keeps a fixed list of screens. Each piece of the session
 * remembers the screens it actually showed, and Back walks them in reverse.
 *
 * THE PIECES
 * - The session page keeps the running order of STAGES (arrival, ground, feel,
 *   see, satori, card, shift, choose, landing, close) and which one is up.
 * - Inside a stage, the stage's own component remembers its own screens
 *   (`useScreenTrail`), or answers Back directly for screens that live outside
 *   its main state (`useSessionBack`: Arrival's warning, Sedona's questions).
 * - Stages that were visited stay MOUNTED but hidden, so going back finds them
 *   exactly as they were left: nothing is rebuilt and no model call repeats.
 *   `ActiveContext` tells a mounted component whether it is the one on screen.
 *
 * ASKING IN ORDER
 * Back asks the innermost thing first (an insight card inside Shift, the
 * grounding exercise inside Close, Sedona's second question inside Choose's
 * Sedona screen), then the stage, then the page moves to the previous stage.
 * Each handler answers one of two things:
 *   'handled'  it moved to an earlier screen of its own (or deliberately did
 *              nothing, see below); Back is done.
 *   'first'    it is at its first screen; ask the next one out.
 * The order is by an explicit DEPTH, never by registration order: a child's
 * effects run before its parent's, so the insight card inside Shift registers
 * before Shift does, and nothing can be assumed from who registered first.
 */
import { createContext, useContext, useEffect, useRef } from 'react'

/**
 * Is the screen this component belongs to the one the person is looking at?
 *
 * True by default so a component used on its own (in a test, say) behaves as it
 * always did. A stage that has been visited and left is mounted but not active:
 * it must not answer Back, and must not put its screen number in the corner
 * stamp, because the person is not looking at it.
 */
export const ActiveContext = createContext(true)

/** What a handler says when asked to go back. */
export type BackResult = 'handled' | 'first'

/**
 * How deep a handler sits. Bigger is asked first.
 *   trail    a component's own run of screens (`useScreenTrail`)
 *   step     a screen with steps outside the component's main state
 *   hosted   a whole component shown inside another (insight card, grounding)
 *   hostedStep  a step inside a hosted component that its own trail does not
 *            see (the grounding exercise's five senses)
 *   overlay  something laid over a screen, which Back closes before anything
 *            underneath it moves (the insight card's image preview)
 */
export const BACK_DEPTH = { trail: 10, step: 20, hosted: 30, hostedStep: 35, overlay: 40 } as const

interface BackEntry {
  depth: number
  run: () => BackResult
}

/** Where handlers sign in. The session page owns the one registry. */
export interface BackRegistry {
  register: (entry: BackEntry) => () => void
}

export const SessionBackContext = createContext<BackRegistry | null>(null)

/**
 * Answer Back for as long as this component is on screen.
 *
 * `run` is called with whatever the component knows at the moment Back is
 * pressed (it is read through a ref, so it never goes stale), and must return
 * 'handled' if it moved to an earlier screen of its own, or 'first' if it has
 * nowhere earlier to go.
 */
export function useSessionBack(run: () => BackResult, depth: number = BACK_DEPTH.step) {
  const active = useContext(ActiveContext)
  const registry = useContext(SessionBackContext)

  const latest = useRef(run)
  useEffect(() => { latest.current = run })

  useEffect(() => {
    // A hidden component answers nothing: Back is for the screen being shown.
    if (!active || !registry) return
    return registry.register({ depth, run: () => latest.current() })
  }, [active, registry, depth])
}

/**
 * What a screen is, as far as Back is concerned.
 *   rest     the person is looking at something they can act on. It goes in
 *            the trail and is a place Back can return to.
 *   loading  the guide is still writing it. Never a Back target, and Back
 *            pressed meanwhile does nothing, like the arrow being disabled
 *            during a hand-over: there is nothing finished to go back from.
 *   error    the "something went wrong" screen. Not a place to return to, but
 *            it can be left: Back goes to the last screen that did work.
 */
export type ScreenKind = 'rest' | 'loading' | 'error'

interface TrailOptions<S extends string, C> {
  /** The screen showing now. Usually the component's `uiState`. */
  screen: S
  /** Show a screen. Usually the component's `setUiState`. */
  go: (screen: S) => void
  /** What kind of screen this is; defaults to 'rest' for every screen. */
  kind?: (screen: S) => ScreenKind
  /**
   * For screens that share one slot of state (the Work's four questions, RAIN's
   * steps): read the slot, so each screen comes back with its own question and
   * its own answer. Called after every render while a screen is at rest, so the
   * snapshot is always what the person last saw, even if the Continue that left
   * the screen then cleared the slot.
   */
  capture?: () => C
  /** Put a captured slot back when Back returns to its screen. */
  restore?: (snapshot: C) => void
  /**
   * How deep this trail sits. A phase's own trail is the default; a component
   * shown inside another (the insight card, the grounding exercise) passes
   * `BACK_DEPTH.hosted` so Back asks it before the phase that hosts it.
   */
  depth?: number
}

/**
 * Remember the resting screens a component shows, in order, and take Back
 * through them in reverse while the component is on screen.
 *
 * The component keeps all its other state (what was typed, what the guide said)
 * exactly as it was, because it is never unmounted; Back only has to say which
 * screen to show. That is why this hook stores screen names and not copies of
 * the component's state.
 */
export function useScreenTrail<S extends string, C = undefined>(options: TrailOptions<S, C>) {
  const trail = useRef<Array<{ screen: S; snapshot: C | undefined }>>([])
  const latest = useRef(options)

  // After every render: note the newest options, and record the screen if it is
  // a resting one. Runs whether or not the component is on screen, because a
  // hidden stage can still finish loading something and move on by itself.
  useEffect(() => {
    latest.current = options
    const { screen, kind, capture } = options
    if ((kind ? kind(screen) : 'rest') !== 'rest') return
    const top = trail.current[trail.current.length - 1]
    if (!top || top.screen !== screen) trail.current.push({ screen, snapshot: capture?.() })
    else if (capture) top.snapshot = capture()
  })

  useSessionBack(() => {
    const { screen, go, kind, restore } = latest.current
    const now = kind ? kind(screen) : 'rest'
    // Nothing finished to go back from. Doing nothing is the answer, and it is
    // 'handled', not 'first': otherwise Back would skip past the whole stage.
    if (now === 'loading') return 'handled'

    const screens = trail.current
    // From a resting screen the way back is the entry below it (and the current
    // one is dropped, so going forward again records it afresh). From an error
    // screen, which is not in the trail, it is the newest entry itself.
    const target = now === 'rest' ? screens[screens.length - 2] : screens[screens.length - 1]
    if (!target) return 'first'
    if (now === 'rest') screens.pop()
    go(target.screen)
    if (target.snapshot !== undefined) restore?.(target.snapshot)
    return 'handled'
  }, options.depth ?? BACK_DEPTH.trail)
}
